import { useCallback, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

/**
 * Confirmación de una acción, con el diálogo del sistema de diseño.
 *
 * El ERP tenía **29 confirmaciones nativas del navegador** repartidas por 21
 * ficheros, y entre ellas la anulación de una factura ante la AEAT. Un
 * `window.confirm` no lleva el tema, no se traduce, no distingue lo reversible
 * de lo que no lo es y no puede pedir nada más que aceptar o cancelar. No había
 * ninguna pieza compartida para sustituirlo: cada sitio que quería algo mejor
 * montaba su propio `AlertDialog` a mano (48 ficheros lo hacen).
 *
 * ## Uso
 *
 *     const { confirm, confirmDialog } = useConfirm();
 *
 *     const handleDelete = async () => {
 *       const ok = await confirm({
 *         title: '¿Eliminar este item?',
 *         tone: 'destructive',
 *       });
 *       if (!ok) return;
 *       …
 *     };
 *
 *     return (<>{confirmDialog}<Button onClick={handleDelete}/></>);
 *
 * Devuelve una promesa a propósito: sustituye `if (!window.confirm(…)) return;`
 * por `if (!(await confirm({…}))) return;` sin darle la vuelta al flujo del
 * llamante, que es lo que hace inviables las migraciones a base de estado.
 *
 * ## `requireText`
 *
 * Para lo que no tiene vuelta atrás —un registro de anulación ante la Agencia
 * Tributaria, por ejemplo— se puede exigir que el usuario escriba un valor
 * exacto para habilitar el botón. Hasta ahora la única pantalla que lo pedía era
 * el offboarding de empresa, que **es reversible**: la asimetría iba justo al
 * revés de lo que debería.
 */
export function useConfirm() {
  const [pending, setPending] = useState(null);
  const [typed, setTyped] = useState('');
  const resolveRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    setTyped('');
    setPending(options);
    return new Promise((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const settle = useCallback((value) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setPending(null);
    setTyped('');
    resolve?.(value);
  }, []);

  const options = pending ?? {};
  const {
    title,
    description,
    confirmLabel = 'Confirmar',
    cancelLabel = 'Cancelar',
    tone = 'default',
    requireText = null,
    requireTextLabel = null,
  } = options;

  const satisfied = !requireText || typed.trim() === String(requireText).trim();

  const confirmDialog = (
    <AlertDialog
      open={pending !== null}
      // Cerrar por Escape o por click fuera equivale a cancelar: la promesa debe
      // resolverse igualmente, o el llamante se queda esperando para siempre.
      onOpenChange={(open) => { if (!open) settle(false); }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description ? (
            <AlertDialogDescription>{description}</AlertDialogDescription>
          ) : null}
        </AlertDialogHeader>

        {requireText ? (
          <div className="space-y-2 py-2">
            <Label htmlFor="confirm-require-text" className="text-body-sm">
              {requireTextLabel}
            </Label>
            <Input
              id="confirm-require-text"
              data-testid="confirm-require-text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
            />
          </div>
        ) : null}

        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-cancel" onClick={() => settle(false)}>
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="confirm-accept"
            disabled={!satisfied}
            className={cn(
              tone === 'destructive' &&
                'bg-destructive text-destructive-foreground hover:bg-destructive/90'
            )}
            onClick={(e) => {
              // Con `requireText` sin satisfacer el botón está deshabilitado, pero
              // Radix cierra el diálogo al activar la acción: si algo lo disparara
              // igualmente (Enter, un test), esto evita confirmar sin el texto.
              if (!satisfied) { e.preventDefault(); return; }
              settle(true);
            }}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { confirm, confirmDialog };
}

export default useConfirm;
